import { Component, DestroyRef, HostListener, OnInit } from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MatCardModule } from "@angular/material/card"
import { MatIconModule } from "@angular/material/icon"
import { MatButtonModule } from "@angular/material/button"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatInputModule } from "@angular/material/input"
import { MatSelectModule } from "@angular/material/select"
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator"
import { MatDialog } from "@angular/material/dialog"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { ActivatedRoute, Router, RouterModule } from "@angular/router"

import { RecordingService } from "../../services/recording.service"
import { PermissionService } from "../../services/permission.service"
import { NotificationService } from "../../services/production-request.service"
import { SnackbarService } from "../../services/snackbar.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { ProfileService } from "../../services/profile.service"
import { ProductType } from "../../models/product-type.model"
import { Recording, RECORDING_STATUS_LABELS, RECORDING_STATUS_CLASS } from "../../models/recording.model"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { RecordingApproveModalComponent, RecordingApproveModalResult } from "./recording-approve-modal.component"
import { RecordingDeclineModalComponent, RecordingDeclineModalResult } from "./recording-decline-modal.component"
import {
  RecordingFeedbackDialogComponent,
  RecordingFeedbackDialogResult,
} from "./recording-feedback-dialog.component"
import {
  RecordingEditDialogComponent,
  RecordingEditDialogResult,
} from "./recording-edit-dialog.component"
import { OwnerStationFiltersComponent } from "../../shared/owner-station-filters/owner-station-filters.component"
import { OwnerStationFilterValues } from "../../models/filter.model"

@Component({
  selector: "app-recordings-list",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    RouterModule,
    PageHeaderComponent,
    OwnerStationFiltersComponent,
  ],
  templateUrl: "./recordings-list.component.html",
  styleUrls: ["./recordings-list.component.css"],
})
export class RecordingsListComponent implements OnInit {

  recordings: Recording[] = []
  loading = false

  totalRecordings = 0
  pageSize = 10
  pageIndex = 0

  statusFilter: number | "" = ""
  lengthFilter: number | "" = ""
  /** "" = all recordings; "true" = approved preemptible promos only */
  preemptibleFilter: "" | "true" = ""
  search = ""

  ownerFilterId: number | null = null
  stationFilterId: number | null = null
  showOwnerStationFilters = false

  userRole = ""
  canApprove = false

  statusLabels = RECORDING_STATUS_LABELS
  statusClass = RECORDING_STATUS_CLASS

  readonly statusOptions: { value: number | ""; label: string }[] = [
    { value: "", label: "All" },
    { value: 0, label: "Pending" },
    { value: 1, label: "Approved" },
    { value: 2, label: "Declined" },
  ]

  readonly lengthOptions: { value: number | ""; label: string }[] = [
    { value: "", label: "Any" },
    { value: 15, label: "15s" },
    { value: 30, label: "30s" },
    { value: 60, label: "60s" },
  ]

  readonly playbackSpeeds = [0.75, 1, 1.25, 1.5, 2]

  /** Set when opening /recordings?recordingId=… from notification deep link. */
  highlightedRecordingId: number | null = null
  private highlightClearTimeout: ReturnType<typeof setTimeout> | null = null
  /** Recording row with approve dialog open — hide list action buttons. */
  approvalDialogRecordingId: number | null = null
  downloadingId: number | null = null
  private currentUserId: number | null = null

  /** Custom player state — one active clip at a time. */
  playingId: number | null = null
  speedMenuOpenId: number | null = null
  private audioEls = new Map<number, HTMLAudioElement>()
  private currentTimes = new Map<number, number>()
  private durations = new Map<number, number>()
  private mutedIds = new Set<number>()
  private playbackRates = new Map<number, number>()

  constructor(
    private service: RecordingService,
    private perms: PermissionService,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef,
    private notificationService: NotificationService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private confirmDialog: ConfirmDialogService,
    private profile: ProfileService,
  ) {}

  /** Re-derived whenever the selected station (and therefore the effective role) changes. */
  private applyRolePermissions(): void {
    this.userRole = this.perms.getCurrentRole()

    const stationStaffRoles = ["admin", "owner", "station_owner", "station_user"]
    this.showOwnerStationFilters = this.perms.hasAnyRole(stationStaffRoles)
    this.canApprove = this.perms.hasAnyRole(stationStaffRoles)
  }

  ngOnInit(): void {
    this.currentUserId = this.profile.getUserId()

    // Switching the active station changes the effective role in place, without a
    // route change, so these flags must be re-derived on every emission rather
    // than snapshotted once. currentRole$ is a BehaviorSubject, so this also
    // performs the initial synchronous assignment.
    this.perms.currentRole$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.applyRolePermissions()
    })

    let queryHookInitial = true
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (queryHookInitial) {
        queryHookInitial = false
        this.load()
        return
      }
      const rid = this.route.snapshot.queryParamMap.get("recordingId")
      if (rid != null && rid !== "") {
        this.load()
      }
    })
  }

  // =========================
  // LOAD
  // =========================
  load() {
    this.pauseAllPlayers()
    this.loading = true

    const params: any = {
      limit: this.pageSize,
      offset: this.pageIndex * this.pageSize,
    }

    if (this.statusFilter !== "") params.status = Number(this.statusFilter)
    if (this.lengthFilter !== "") params.length = Number(this.lengthFilter)
    if (this.preemptibleFilter === "true") params.preemptible_only = true
    if (this.search.trim()) params.name = this.search.trim()
    if (this.ownerFilterId != null) params.owner_id = this.ownerFilterId
    if (this.stationFilterId != null) params.station_id = this.stationFilterId

    this.service.list(params).subscribe({
      next: (res) => {
        const records = Array.isArray(res.records) ? res.records : []

        this.totalRecordings = res.pagination?.total || records.length

        this.recordings = records
          .filter((r) => r != null && typeof r === "object")
          .map((r) => {
            const row = r as Recording & { audio_url?: string }
            const fromId = Number(row.resubmitted_from_recording_id)
            return {
              ...row,
              presigned_url: row.audio_url,
              product_types: Array.isArray(row.product_types) ? row.product_types : [],
              is_superseded: !!row.is_superseded,
              resubmitted_from_recording_id: Number.isFinite(fromId) && fromId > 0 ? fromId : null,
            }
          })

        this.loading = false
        this.applyRecordingNotificationDeepLink()
      },
      error: () => (this.loading = false),
    })
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize
    this.pageIndex = event.pageIndex
    this.load()
  }

  onFilterChange() {
    this.pageIndex = 0
    this.load()
  }

  onOwnerStationFilterChange(values: OwnerStationFilterValues): void {
    this.ownerFilterId = values.ownerId
    this.stationFilterId = values.stationId
    this.onFilterChange()
  }

  setStatusFilter(value: number | ""): void {
    if (this.statusFilter === value) return
    this.statusFilter = value
    this.onFilterChange()
  }

  clearSearch(): void {
    if (!this.search) return
    this.search = ""
    this.onFilterChange()
  }

  resetFilters(): void {
    this.statusFilter = ""
    this.lengthFilter = ""
    this.preemptibleFilter = ""
    this.search = ""
    this.onFilterChange()
  }

  get hasActiveFilters(): boolean {
    return (
      this.statusFilter !== "" ||
      this.lengthFilter !== "" ||
      this.preemptibleFilter === "true" ||
      this.search.trim() !== ""
    )
  }

  get rangeStart(): number {
    return this.totalRecordings === 0 ? 0 : this.pageIndex * this.pageSize + 1
  }

  get rangeEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.totalRecordings)
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (this.speedMenuOpenId == null) return
    const target = event.target as HTMLElement | null
    if (target?.closest(".rad-player__speed")) return
    this.speedMenuOpenId = null
  }

  registerAudio(r: Recording, event: Event): void {
    const id = this.recordingDomId(r)
    const el = event.target as HTMLAudioElement
    if (id == null || !el) return
    this.audioEls.set(id, el)
    const duration = Number.isFinite(el.duration) && el.duration > 0
      ? el.duration
      : Number(r.audiolength) || 0
    this.durations.set(id, duration)
    el.playbackRate = this.playbackRates.get(id) ?? 1
    el.muted = this.mutedIds.has(id)
  }

  onAudioTimeUpdate(r: Recording, event: Event): void {
    const id = this.recordingDomId(r)
    const el = event.target as HTMLAudioElement
    if (id == null || !el) return
    this.currentTimes.set(id, el.currentTime || 0)
    if (Number.isFinite(el.duration) && el.duration > 0) {
      this.durations.set(id, el.duration)
    }
  }

  onAudioEnded(r: Recording): void {
    const id = this.recordingDomId(r)
    if (id == null) return
    this.playingId = null
    this.currentTimes.set(id, 0)
    const el = this.audioEls.get(id)
    if (el) el.currentTime = 0
  }

  onAudioPlay(r: Recording): void {
    const id = this.recordingDomId(r)
    if (id == null) return
    this.pauseAllPlayers(id)
    this.playingId = id
  }

  onAudioPause(r: Recording): void {
    const id = this.recordingDomId(r)
    if (id != null && this.playingId === id) {
      this.playingId = null
    }
  }

  togglePlay(r: Recording): void {
    const id = this.recordingDomId(r)
    if (id == null) return
    const el = this.audioEls.get(id)
    if (!el) {
      this.snackbar.error("Audio is not ready yet.")
      return
    }

    if (this.playingId === id && !el.paused) {
      el.pause()
      this.playingId = null
      return
    }

    this.pauseAllPlayers(id)
    void el.play().then(() => {
      this.playingId = id
    }).catch(() => {
      this.snackbar.error("Unable to play this recording.")
      this.playingId = null
    })
  }

  seekAudio(r: Recording, event: Event): void {
    const id = this.recordingDomId(r)
    const el = this.audioEls.get(id ?? -1)
    const input = event.target as HTMLInputElement
    if (id == null || !el || !input) return
    const next = Number(input.value)
    if (!Number.isFinite(next)) return
    el.currentTime = next
    this.currentTimes.set(id, next)
  }

  toggleMute(r: Recording): void {
    const id = this.recordingDomId(r)
    const el = this.audioEls.get(id ?? -1)
    if (id == null || !el) return
    const muted = !el.muted
    el.muted = muted
    if (muted) this.mutedIds.add(id)
    else this.mutedIds.delete(id)
  }

  toggleSpeedMenu(r: Recording, event: Event): void {
    event.stopPropagation()
    const id = this.recordingDomId(r)
    if (id == null) return
    this.speedMenuOpenId = this.speedMenuOpenId === id ? null : id
  }

  setPlaybackSpeed(r: Recording, speed: number, event: Event): void {
    event.stopPropagation()
    const id = this.recordingDomId(r)
    const el = this.audioEls.get(id ?? -1)
    if (id == null || !el) return
    el.playbackRate = speed
    this.playbackRates.set(id, speed)
    this.speedMenuOpenId = null
  }

  isPlaying(r: Recording): boolean {
    const id = this.recordingDomId(r)
    return id != null && this.playingId === id
  }

  isMuted(r: Recording): boolean {
    const id = this.recordingDomId(r)
    return id != null && this.mutedIds.has(id)
  }

  isSpeedMenuOpen(r: Recording): boolean {
    const id = this.recordingDomId(r)
    return id != null && this.speedMenuOpenId === id
  }

  currentTime(r: Recording): number {
    const id = this.recordingDomId(r)
    return id != null ? (this.currentTimes.get(id) ?? 0) : 0
  }

  duration(r: Recording): number {
    const id = this.recordingDomId(r)
    if (id == null) return Number(r.audiolength) || 0
    return this.durations.get(id) ?? (Number(r.audiolength) || 0)
  }

  progress(r: Recording): number {
    const total = this.duration(r)
    if (total <= 0) return 0
    return Math.min(100, (this.currentTime(r) / total) * 100)
  }

  playbackRate(r: Recording): number {
    const id = this.recordingDomId(r)
    return id != null ? (this.playbackRates.get(id) ?? 1) : 1
  }

  formatPlayerTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00"
    const whole = Math.floor(seconds)
    const m = Math.floor(whole / 60)
    const s = whole % 60
    return `${m}:${s.toString().padStart(2, "0")}`
  }

  private pauseAllPlayers(exceptId?: number): void {
    this.audioEls.forEach((el, id) => {
      if (exceptId != null && id === exceptId) return
      if (!el.paused) el.pause()
    })
    if (exceptId == null || this.playingId !== exceptId) {
      this.playingId = null
    }
    this.speedMenuOpenId = null
  }

  // =========================
  // APPROVAL ACTIONS
  // =========================
  approve(r: Recording) {
    if (r.approval_status !== 0 || r.id == null || r.station_id == null) return

    const recordingId = this.recordingDomId(r)
    if (recordingId == null) return

    this.approvalDialogRecordingId = recordingId

    const ref = this.dialog.open(RecordingApproveModalComponent, {
      width: "680px",
      maxWidth: "94vw",
      maxHeight: "92vh",
      panelClass: ["app-confirm-dialog", "recording-approve-dialog"],
      autoFocus: "first-titled-element",
      disableClose: false,
      data: {
        recordingName: r.name || `Recording #${r.id}`,
        recordingId: r.id,
        stationId: r.station_id,
      },
    })

    ref.afterClosed().subscribe((result: RecordingApproveModalResult | undefined) => {
      this.approvalDialogRecordingId = null
      if (!result?.success) return

      this.snackbar.success("Recording approved successfully. An email confirmation has been sent to you.")
      // Reload so chips come from embedded product_types on GET /audioRecordings
      this.load()
    })
  }

  isApprovalDialogOpenFor(r: Recording): boolean {
    const id = this.recordingDomId(r)
    return id != null && this.approvalDialogRecordingId === id
  }

  decline(r: Recording) {
    if (r.approval_status !== 0 || r.id == null || r.station_id == null) return

    const ref = this.dialog.open(RecordingDeclineModalComponent, {
      width: "560px",
      maxWidth: "94vw",
      panelClass: ["app-confirm-dialog", "recording-decline-dialog"],
      autoFocus: "first-tabbable",
      data: {
        recordingName: r.name || `Recording #${r.id}`,
        recordingId: r.id,
        stationId: r.station_id,
      },
    })

    ref.afterClosed().subscribe((result: RecordingDeclineModalResult | undefined) => {
      if (!result?.success) return
      this.snackbar.info("Recording declined. Please check your email for details.")
      this.load()
    })
  }

  isDeclinedRecording(r: Recording): boolean {
    return r.approval_status === 2
  }

  isSupersededRecording(r: Recording): boolean {
    return !!r.is_superseded
  }

  /**
   * Declined and still open, or a new recording that inherited the decline thread.
   * Only the uploader and staff of THIS card's station take part in its thread.
   */
  canViewFeedback(r: Recording): boolean {
    if (this.isSupersededRecording(r)) return false
    if (r.station_id == null) return false
    if (!this.isUploaderOf(r) && !this.canModerateStation(r)) return false
    if (this.isDeclinedRecording(r)) return true
    const fromId = Number(r.resubmitted_from_recording_id)
    return Number.isFinite(fromId) && fromId > 0
  }

  private isUploaderOf(r: Recording): boolean {
    return this.currentUserId != null && r.uploaderid != null && Number(r.uploaderid) === Number(this.currentUserId)
  }

  /** Station staff assigned to this card's station (admin always). */
  private canModerateStation(r: Recording): boolean {
    return this.canApprove && this.perms.canActOnStation(r.station_id)
  }

  statusBadgeLabel(r: Recording): string {
    if (this.isSupersededRecording(r)) return "Superseded"
    return this.statusLabels[r.approval_status]
  }

  statusBadgeClass(r: Recording): string {
    if (this.isSupersededRecording(r)) return "status-superseded"
    return this.statusClass[r.approval_status]
  }

  openFeedback(r: Recording): void {
    const recordingId = this.recordingDomId(r)
    if (recordingId == null || !this.canViewFeedback(r)) return

    const ref = this.dialog.open(RecordingFeedbackDialogComponent, {
      width: "min(640px, 94vw)",
      maxWidth: "94vw",
      maxHeight: "92vh",
      panelClass: ["app-confirm-dialog", "recording-feedback-dialog"],
      autoFocus: "dialog",
      data: { recording: r },
    })

    ref.afterClosed().subscribe((result: RecordingFeedbackDialogResult | undefined) => {
      if (!result?.submitted) return
      const station = result.stationName || r.station_name || "the station"
      this.snackbar.success(`Your corrected recording has been resubmitted to ${station} for review.`)
      this.load()
    })
  }

  remove(r: Recording) {
    this.confirmDelete(r)
  }

  openEdit(r: Recording): void {
    const recordingId = this.recordingDomId(r)
    if (recordingId == null || !this.canEditRecording(r)) return

    const ref = this.dialog.open(RecordingEditDialogComponent, {
      width: "min(560px, 94vw)",
      maxWidth: "94vw",
      maxHeight: "92vh",
      panelClass: ["app-confirm-dialog", "recording-edit-dialog"],
      autoFocus: "first-tabbable",
      data: { recording: r },
    })

    ref.afterClosed().subscribe((result: RecordingEditDialogResult | undefined) => {
      if (!result?.success) return
      this.applyEditedRecordingUpdate(result.recording)
      this.snackbar.success("Recording updated successfully.")
    })
  }

  confirmDelete(r: Recording): void {
    const recordingId = this.recordingDomId(r)
    if (recordingId == null || !this.canDeleteRecording(r)) return

    this.confirmDialog
      .open({
        title: "Delete recording",
        message: `Are you sure you want to delete "${r.name || `Recording #${recordingId}`}"? This cannot be undone.`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
        confirmIcon: "delete",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.service.delete(recordingId).subscribe({
          next: () => {
            this.snackbar.success("Recording deleted successfully.")
            this.load()
          },
          error: (err) => {
            const message =
              err?.error?.message ||
              err?.error?.error ||
              "Unable to delete this recording."
            this.snackbar.error(message, 6000)
          },
        })
      })
  }

  async downloadRecording(r: Recording): Promise<void> {
    const url = r.presigned_url || r.audio_url
    const recordingId = this.recordingDomId(r)
    if (!url || recordingId == null) {
      this.snackbar.error("Download is unavailable for this recording.")
      return
    }

    this.downloadingId = recordingId
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`)
      }
      const blob = await response.blob()
      const extension = this.guessAudioExtension(url, blob.type)
      const safeName = (r.name || `recording-${recordingId}`)
        .replace(/[^\w.\-]+/g, "_")
        .replace(/_+/g, "_")
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = objectUrl
      link.download = `${safeName}${extension}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch {
      // Fallback: open in a new tab so the current page is preserved.
      window.open(url, "_blank", "noopener,noreferrer")
    } finally {
      this.downloadingId = null
    }
  }

  private applyEditedRecordingUpdate(updated: RecordingEditDialogResult["recording"]): void {
    this.recordings = this.recordings.map((row) => {
      if (!this.recordingMatchesId(row, updated.id)) return row
      const sameStation =
        updated.station_id != null &&
        row.station_id != null &&
        Number(row.station_id) === Number(updated.station_id)
      return {
        ...row,
        name: updated.name,
        talentid: updated.talentid ?? row.talentid,
        audiolength: updated.audiolength ?? row.audiolength,
        audio_url: updated.audio_url || row.audio_url,
        presigned_url: updated.audio_url || row.presigned_url,
        product_types:
          sameStation && updated.product_types !== undefined
            ? updated.product_types
            : row.product_types,
        is_preemptible_promo:
          updated.is_preemptible_promo !== undefined
            ? updated.is_preemptible_promo
            : row.is_preemptible_promo,
      }
    })
  }

  private guessAudioExtension(url: string, mimeType: string): string {
    const lowerUrl = url.toLowerCase()
    for (const ext of [".mp3", ".wav", ".m4a", ".aac", ".ogg"]) {
      if (lowerUrl.includes(ext)) return ext
    }
    if (mimeType.includes("wav")) return ".wav"
    if (mimeType.includes("m4a") || mimeType.includes("mp4")) return ".m4a"
    if (mimeType.includes("aac")) return ".aac"
    if (mimeType.includes("ogg")) return ".ogg"
    return ".mp3"
  }

  // =========================
  // UI HELPERS
  // =========================
  canApproveRecording(r: Recording): boolean {
    if (r.approval_status !== 0 || this.isSupersededRecording(r)) return false
    return this.canModerateStation(r)
  }

  canEditRecording(r: Recording): boolean {
    return this.perms.canEditRecording(r, this.currentUserId)
  }

  canDeleteRecording(r: Recording): boolean {
    return this.perms.canDeleteRecording(r, this.currentUserId)
  }

  canDownloadRecording(r: Recording): boolean {
    return !!(r.presigned_url || r.audio_url)
  }

  recordingRowDomId(r: Recording): number {
    return this.recordingDomId(r) ?? 0
  }

  isHighlightedRecording(r: Recording): boolean {
    if (this.highlightedRecordingId == null) return false
    return this.recordingMatchesId(r, this.highlightedRecordingId)
  }

  private recordingMatchesId(r: Recording, id: number): boolean {
    const rowId = this.recordingDomId(r)
    return rowId === id
  }

  productTypeLabel(pt: ProductType): string {
    const name = pt?.name ?? ""
    const description = pt?.description ?? ""
    return `[${name}] ${description}`
  }

  recordingDomId(r: Recording): number | undefined {
    const row = r as Recording & { recordingId?: number | string }
    const raw: unknown = row.id ?? row.recording_id ?? row.recordingId
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw
    }
    if (typeof raw === "string" && raw.trim() !== "") {
      const parsed = Number(raw)
      return Number.isFinite(parsed) ? parsed : undefined
    }
    return undefined
  }

  /**
   * Bell → /recordings?recordingId=:id — scroll, highlight, mark notifications read, strip query.
   */
  private applyRecordingNotificationDeepLink(): void {
    const raw = this.route.snapshot.queryParamMap.get("recordingId")
    if (raw == null || raw === "") return
    const focusId = Number(raw)
    if (!Number.isFinite(focusId)) return

    this.notificationService.markReadByReference(focusId).subscribe({ error: () => {} })

    const onPage = this.recordings.some((r) => this.recordingMatchesId(r, focusId))
    if (onPage) {
      if (this.highlightClearTimeout != null) {
        clearTimeout(this.highlightClearTimeout)
        this.highlightClearTimeout = null
      }
      this.highlightedRecordingId = focusId
      this.highlightClearTimeout = setTimeout(() => {
        this.highlightedRecordingId = null
        this.highlightClearTimeout = null
      }, 5000)

      setTimeout(() => {
        document.getElementById(`recording-item-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 0)
    } else {
      this.snackbar.info(`Recording #${focusId} is not on this page. Try another page or adjust filters.`)
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { recordingId: null },
      queryParamsHandling: "merge",
      replaceUrl: true,
    })
  }
}
